import {
  Component,
  OnInit,
  OnDestroy,
  Input,
  Output,
  EventEmitter,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  OnChanges,
  SimpleChanges,
  ElementRef,
  HostBinding,
  HostListener,
  AfterViewInit
} from '@angular/core';

import { resources } from './resources';
import { TranslateHelperService } from '../../../translation/services/translate-helper.service';
import { Direction } from '../../interfaces';

const CONSTRAINT_PADDING_PX = 8;
// Used by legacy applyCenterArrow to position the balloon
const CENTER_ARROW_PADDING_PX = 12;
const CENTER_ARROW_WIDTH_PX = 20;

@Component({
  selector: 'app-vi-notification-tooltip',
  templateUrl: './notification-tooltip.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./notification-tooltip.component.scss'],
  standalone: false
})
export class NotificationTooltipComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @Input() public notificationText = '';
  @Input() public duration = 3000;
  @Input() public hasExitButton = false;
  @Input() public addOverlay = true;
  @Input() public hasCheckbox = false;
  @Input() public checkboxText = '';
  @Input() public checkboxTextId: string;
  @Input() public addAnimation = true;
  // when stretch true the tooltip width wil be according to the content
  @Input() public stretch = false;
  @Input() public maxWidth: string;
  @Input() public direction = Direction.RIGHT;
  // Pass target element to locate the tooltip when using left direction/relocate right direction/center arrow
  @Input() public targetElement: ElementRef;
  // center the tooltip arrow to the center of the target element
  @Input() public centerArrow = false;
  @Input() public tooltipClass = '';
  @Input() public showArrow = true;
  @Input() public showNotificationDown = false;
  @Input() public ignoreApplyDirection = false;
  // When true, clamp tooltip position/width within nearest overflow ancestor and compensate arrow
  @Input() public adaptiveConstraint = false;

  @Output() public notificationDismiss = new EventEmitter<void>();
  @Output() public selectCheckboxOutput = new EventEmitter<boolean>();
  @Output() public escPressed = new EventEmitter<boolean>();

  @HostBinding('style.left') public leftStyle: string;
  @HostBinding('style.top') public topStyle: string;
  @HostBinding('style.right') public rightStyle: string;
  @HostBinding('style.maxWidth') public hostMaxWidth: string;
  @HostBinding('class.is-visible') public isVisible: boolean;

  public startAnimation = false;
  public checkboxSelected = false;
  public resources = resources;

  public get effectiveSectionMaxWidth(): string {
    return this.clampedMaxWidth || this.maxWidth || null;
  }

  private dismissTimeout: ReturnType<typeof setTimeout>;
  private animationTimeout: ReturnType<typeof setTimeout>;
  private animationDuration = 500;
  private applyDirectionRequestId = 0;
  private clampedMaxWidth: string;

  constructor(private cdr: ChangeDetectorRef, private translate: TranslateHelperService, private el: ElementRef) {}

  public ngOnInit() {
    // Translate resources
    this.translate.translateResources(this.resources);
  }

  public ngOnDestroy(): void {
    clearTimeout(this.dismissTimeout);
    clearTimeout(this.animationTimeout);
  }

  @HostListener('document:keydown.escape', ['$event']) public onKeyupHandler(event: KeyboardEvent) {
    this.escPressed.emit();
  }

  @HostListener('window:resize') public onWindowResize() {
    if (!this.isVisible || !this.hasTooltipContent()) {
      return;
    }

    this.applyDirection().then(() => {
      this.cdr.markForCheck();
    });
  }

  public ngAfterViewInit(): void {
    // Set tooltip direction must be in AfterViewInit so view will be loaded first
    this.applyDirection().then(() => {
      this.cdr.markForCheck();
      this.isVisible = true;
    });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes && changes.notificationText) {
      // Text has been changed, apply timeout again
      clearTimeout(this.animationTimeout);
      clearTimeout(this.dismissTimeout);

      this.isVisible = false;
      this.initNotification();
    }
  }

  public applyDirection(): Promise<void> {
    const requestId = ++this.applyDirectionRequestId;
    // Reset overflow-related state from previous runs
    // Start with the input maxWidth on the host (if set); fitToConstraint may shrink it further
    this.hostMaxWidth = this.maxWidth || null;
    this.clampedMaxWidth = null;
    const measuredLeftValue = parseFloat(this.leftStyle) || 0;

    const tooltipElement = this.el.nativeElement.getBoundingClientRect();
    const bodyRect = document.body.getBoundingClientRect();

    return Promise.resolve().then(() => {
      if (requestId !== this.applyDirectionRequestId) {
        return;
      }
      // apply direction if needed
      switch (this.direction) {
        case Direction.RIGHT:
          this.locateRightDirectionTooltip(tooltipElement, bodyRect);
          break;
        case Direction.LEFT:
          this.locateLeftDirectionTooltip(tooltipElement, bodyRect);
          break;
      }

      if (this.adaptiveConstraint) {
        // Clamp position to prevent overflow outside visible area
        this.fitToConstraint(tooltipElement, measuredLeftValue);
      }

      // when tooltip over body element bottom, show tooltip above the target element
      if (!this.ignoreApplyDirection && tooltipElement?.bottom > bodyRect?.bottom && !this.showNotificationDown) {
        this.topStyle = `-${tooltipElement?.height}px`;
      }

      // Position arrow at target after all balloon positioning is done
      if (this.centerArrow && this.targetElement?.nativeElement && this.showArrow) {
        this.positionArrowAtTarget();
      }
    });
  }

  public dismiss(): void {
    if (this.dismissTimeout) {
      clearTimeout(this.dismissTimeout);
    }

    this.startAnimation = false;
    this.cdr.markForCheck();

    this.notificationText = '';
    this.notificationDismiss.emit();
  }

  public selectCheckbox() {
    this.selectCheckboxOutput.emit(this.checkboxSelected);
  }

  public get checkboxTextElementId() {
    return `notification${this.checkboxTextId}`;
  }

  private initNotification() {
    // Show popup according to duration input
    if (this.addAnimation) {
      this.animationTimeout = setTimeout(() => {
        this.startAnimation = true;
        this.cdr.markForCheck();

        this.dismissTimeout = setTimeout(() => {
          this.dismiss();
        }, this.animationDuration);
      }, this.duration);
    }
  }

  private hasTooltipContent(): boolean {
    if (this.notificationText) {
      return true;
    }

    return !!(this.el.nativeElement as HTMLElement).querySelector('[notificationContent]');
  }

  private locateRightDirectionTooltip(tooltipRect: DOMRect, bodyRect: DOMRect) {
    let leftLocation = 0;
    if (this.centerArrow) {
      leftLocation = this.applyCenterArrow(0, Direction.RIGHT);
    }
    // check if relocate (change direction) is needed
    if (tooltipRect.right + leftLocation > bodyRect.right) {
      this.changeDirection(Direction.LEFT, tooltipRect);
    }
  }

  private changeDirection(newDirection: Direction, tooltipRect: DOMRect) {
    this.direction = newDirection;
    let leftLocation = 0;
    switch (newDirection) {
      case Direction.LEFT:
        leftLocation = this.applyLeftDirection(tooltipRect);
        break;
      case Direction.RIGHT:
        this.leftStyle = '0px';
        break;
    }

    if (this.centerArrow) {
      leftLocation = this.applyCenterArrow(leftLocation, newDirection);
    }

    this.cdr.detectChanges();
  }

  private locateLeftDirectionTooltip(tooltipRect: DOMRect, bodyRect: DOMRect) {
    let leftLocation = this.applyLeftDirection(tooltipRect);

    if (this.centerArrow) {
      leftLocation = this.applyCenterArrow(leftLocation, Direction.LEFT);
    }

    const tooltipLeft = tooltipRect.left + leftLocation;
    // check if relocate (change direction) is needed
    if (tooltipLeft < bodyRect.left) {
      this.changeDirection(Direction.RIGHT, tooltipRect);
    }
  }

  private applyLeftDirection(tooltip: DOMRect): number {
    if (this.direction === Direction.LEFT && this.targetElement?.nativeElement) {
      const tooltipTargetGap = Math.abs(tooltip.width - this.targetElement.nativeElement.offsetWidth);
      this.leftStyle = `-${tooltipTargetGap}px`;
      return -tooltipTargetGap;
    }
    return 0;
  }

  /**
   * Walk up the DOM to find the nearest ancestor with overflow clipping.
   * This is the actual boundary that clips the absolutely-positioned tooltip,
   * NOT document.body.
   *
   * @note Uses direct DOM access (parentElement, getComputedStyle, getBoundingClientRect)
   * which bypasses Angular's rendering abstraction. Not compatible with SSR.
   */
  private getClippingAncestorRect(): DOMRect {
    let el = this.el.nativeElement.parentElement;
    while (el && el !== document.body) {
      const style = window.getComputedStyle(el);
      const overflowX = style.overflowX;
      if (overflowX === 'hidden' || overflowX === 'auto' || overflowX === 'scroll') {
        return el.getBoundingClientRect();
      }
      el = el.parentElement;
    }
    return document.body.getBoundingClientRect();
  }

  /**
   * After direction/centerArrow have set the ideal tooltip position,
   * clamp it to stay within the nearest clipping ancestor.
   *
   * @note Uses direct DOM reads via getBoundingClientRect() and getComputedStyle
   * which bypass Angular's rendering abstraction. Not compatible with SSR.
   */
  private fitToConstraint(tooltipRect: DOMRect, measuredLeftValue: number): void {
    const constraintRect = this.getClippingAncestorRect();
    const padding = CONSTRAINT_PADDING_PX; // minimum gap from constraint edges
    const constraintLeft = constraintRect.left + padding;
    const constraintRight = constraintRect.right - padding;
    const availableWidth = constraintRight - constraintLeft;

    if (availableWidth <= 0) {
      return; // degenerate case — constraint too small
    }

    // Current left offset as set by direction/centerArrow logic
    const currentLeftValue = parseFloat(this.leftStyle) || 0;

    // tooltipRect was measured with measuredLeftValue applied, so only apply the delta
    // from measuredLeftValue -> currentLeftValue to get the new absolute left.
    const absLeft = tooltipRect.left + (currentLeftValue - measuredLeftValue);

    // Shrink width if wider than available space
    let effectiveWidth = tooltipRect.width;
    if (effectiveWidth > availableWidth) {
      effectiveWidth = availableWidth;
      this.hostMaxWidth = `${effectiveWidth}px`;
      this.clampedMaxWidth = `${effectiveWidth}px`;
    }

    // Clamp position
    let newAbsLeft = absLeft;
    if (newAbsLeft < constraintLeft) {
      newAbsLeft = constraintLeft;
    }
    if (newAbsLeft + effectiveWidth > constraintRight) {
      newAbsLeft = constraintRight - effectiveWidth;
    }

    // How much did clamping move the tooltip?
    const shiftAmount = newAbsLeft - absLeft;

    if (shiftAmount !== 0) {
      this.leftStyle = `${currentLeftValue + shiftAmount}px`;
    }
  }

  /**
   * Position the arrow at the target element center using offset-based coordinates.
   * Both the target and the host are positioned relative to the same container (offsetParent),
   * so we can compute the arrow position without viewport measurements or detectChanges.
   *
   * @note Uses direct DOM access (offsetLeft, style.setProperty) which bypasses Angular's
   * rendering abstraction. Not compatible with SSR. This is intentional for performance —
   * offset-based measurement avoids layout thrashing from getBoundingClientRect.
   */
  private positionArrowAtTarget(): void {
    const arrowEl = (this.el.nativeElement as HTMLElement).querySelector('.tooltip-arrow') as HTMLElement;
    if (!arrowEl || !this.targetElement?.nativeElement) {
      return;
    }

    // Target center relative to the positioned container (offsetParent)
    const targetEl = this.targetElement.nativeElement as HTMLElement;
    const targetCenter = targetEl.offsetLeft + targetEl.offsetWidth / 2;

    // Host left relative to the same container
    const hostLeft = parseFloat(this.leftStyle) || 0;

    // Arrow offset from the host's left edge to the target center
    const arrowOffset = targetCenter - hostLeft;

    arrowEl.style.setProperty('--arrow-offset', `${arrowOffset}px`);
  }

  private applyCenterArrow(leftLocation: number, direction: Direction): number {
    // can't center arrow to target when there is no target
    if (!this.targetElement?.nativeElement) {
      return leftLocation;
    }
    const arrowCenter = CENTER_ARROW_WIDTH_PX / 2;
    const targetCenter = this.targetElement.nativeElement.offsetWidth / 2;
    // direction to move tooltip is according to the tooltip direction
    const shiftDirection = direction === Direction.RIGHT ? -1 : 1;
    leftLocation += shiftDirection * (CENTER_ARROW_PADDING_PX + arrowCenter - targetCenter);
    this.leftStyle = `${leftLocation}px`;
    return leftLocation;
  }
}
